import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { fetchTenantBootstrap } from '../api/bootstrapAPI';
import { captureError } from '../utils/sentry';
import { isValidTenantSlug, getTenantSlugFromPath } from '../api/apiHelpers';
import { safeGetItem, safeSetItem, safeRemoveItem } from '@mediatechgroup/app-version';

interface Tenant {
  id: string;
  tenant_id: string;
  name: string;
  supabase_url?: string;
  supabase_anon_key?: string;
  stripe_public_key?: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
}

export interface TenantContextType {
  tenant: Tenant | null;
  loading: boolean;
  setCurrentTenant: (tenant: Tenant) => void;
  getTenantById: (tenantId: string) => Promise<Tenant | null>;
}

const defaultContext: TenantContextType = {
  tenant: null,
  loading: true,
  setCurrentTenant: () => {},
  getTenantById: async () => null
};

export const TenantContext = createContext<TenantContextType>(defaultContext);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);

  const getTenantById = async (tenantId: string): Promise<Tenant | null> => {
    // Validate tenant ID before making DB call
    if (!isValidTenantSlug(tenantId)) {
      console.warn('Invalid tenant ID requested:', tenantId);
      return null;
    }

    try {
      // Backend bootstrap endpoint — the browser no longer queries the
      // central DB directly (mobile backend-first; a native app boots
      // from the same call). "not_found" (typo'd slug, deleted tenant)
      // is a normal no-match case, not an error worth logging.
      const result = await fetchTenantBootstrap(tenantId);
      if (result.status === 'not_found') return null;
      if (result.status === 'error') {
        captureError(result.error, { component: 'TenantContext', operation: 'getTenantById', tenantId });
        return null;
      }

      const { data } = result;
      return {
        id: String(data.tenant.id ?? ''),
        tenant_id: data.tenant.slug,
        name: data.tenant.name,
        supabase_url: data.supabase.url ?? undefined,
        supabase_anon_key: data.supabase.anonKey ?? undefined,
        stripe_public_key: data.stripe.publishableKey ?? undefined,
        primary_color: data.settings?.primary_color ?? undefined,
        secondary_color: data.settings?.secondary_color ?? undefined,
      } as Tenant;
    } catch (error) {
      captureError(error, { component: 'TenantContext', operation: 'getTenantById', tenantId });
      return null;
    }
  };

  useEffect(() => {
    const loadTenant = async () => {
      // First, try to get tenant ID from URL path
      const tenantIdFromPath = getTenantSlugFromPath();

      // Check if we have a stored tenant that matches the current path.
      // safeGetItem: a raw read throws a SecurityError in a blocked-storage
      // iframe, and because loadTenant is async the rejection is unhandled —
      // setLoading(false) below never runs and the /embed/* route hangs on a
      // spinner forever (CLUB-CENTRAL-TENANT-4P / -4M). Returning null just
      // means "nothing cached", and the path lookup below still resolves it.
      const storedTenant = safeGetItem('currentTenant');

      if (storedTenant) {
        try {
          const parsedTenant = JSON.parse(storedTenant);
          // If URL has a tenant ID and it matches stored tenant, use it
          if (!tenantIdFromPath || parsedTenant.tenant_id === tenantIdFromPath) {
            setTenant(parsedTenant);
            setLoading(false);
            return;
          }
          // URL tenant doesn't match stored tenant, clear and fetch new
          safeRemoveItem('currentTenant');
        } catch (error) {
          captureError(error, { component: 'TenantContext', operation: 'parseStoredTenant' });
          safeRemoveItem('currentTenant');
        }
      }

      // Try to determine tenant from URL path
      if (tenantIdFromPath) {
        const tenantData = await getTenantById(tenantIdFromPath);

        if (tenantData) {
          setTenant(tenantData);
          safeSetItem('currentTenant', JSON.stringify(tenantData));
        }
      }

      setLoading(false);
    };

    loadTenant();
  }, []);

  const setCurrentTenant = (tenantData: Tenant) => {
    setTenant(tenantData);
    safeSetItem('currentTenant', JSON.stringify(tenantData));
  };

  const value = {
    tenant,
    loading,
    setCurrentTenant,
    getTenantById
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};
